import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { Chip, ErrorNote } from './ui.jsx'

const FIELDS = [
  ['name', 'Name', 'text'],
  ['title', 'Title', 'text'],
  ['email', 'Email', 'email'],
  ['phone', 'Phone', 'tel'],
  ['linkedin', 'LinkedIn URL', 'url'],
]

// One contact editor, used from the Contacts list and from an account's
// contacts tab. Both write through the same endpoint, so an edit made in
// either place shows up in the other.
//
// `contact` is the row being edited, or {} to add a new one.
export default function ContactDialog({ slug, accountName, contact, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...contact }))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setForm({ ...contact })
    setError(null)
  }, [contact])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const isNew = !contact?.name
  // Research contacts can be enriched but not deleted: the research found them,
  // and removing one here would quietly disagree with the evidence.
  const removable = !isNew && contact.origin === 'added'

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  // Only what the form holds goes to the server. The decoration the API adds
  // to every row -- origin, is_placeholder, email_pattern -- is not a person's
  // detail and must not be written back as one.
  const payload = () => {
    const out = {}
    for (const [key] of FIELDS) if (form[key] != null && form[key] !== '') out[key] = form[key]
    if (form.note != null) out.note = form.note
    if (form.email_status) out.email_status = form.email_status
    if (form.email_source) out.email_source = form.email_source
    return out
  }

  const save = async () => {
    if (!(form.name || '').trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.upsertContact(slug, payload())
      onSaved?.()
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.removeContact(slug, contact.name)
      onSaved?.()
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="scrim modal-scrim" onClick={onClose} />
      <div className="modal" role="dialog" aria-label={isNew ? 'Add contact' : `Edit ${contact.name}`}>
        <div className="modal-head">
          <span className="card-title">{isNew ? 'Add a contact' : contact.name}</span>
          {contact?.origin === 'added' ? <Chip kind="named">ADDED</Chip> : null}
          <button className="slide-close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="modal-body stack" style={{ gap: 9 }}>
          {accountName ? <div className="card-sub">{accountName}</div> : null}
          <ErrorNote error={error} />

          {FIELDS.map(([key, label, type]) => (
            <label key={key} className="stack" style={{ gap: 3 }}>
              <span className="lbl">{label}</span>
              <input
                className="input"
                type={type}
                value={form[key] || ''}
                onChange={set(key)}
                autoFocus={key === 'name' && isNew}
                readOnly={key === 'name' && !isNew}
                style={key === 'name' && !isNew ? { background: 'var(--line-soft)', color: 'var(--muted)' } : undefined}
              />
            </label>
          ))}

          <label className="stack" style={{ gap: 3 }}>
            <span className="lbl">Notes</span>
            <textarea
              className="input"
              placeholder="Anything worth remembering before the next conversation"
              value={form.note || ''}
              onChange={set('note')}
            />
          </label>

          {form.email && form.email_status !== 'verified' ? (
            <div style={{ background: 'var(--warn-soft)', border: '0.5px solid var(--warn)', padding: '8px 10px' }}>
              <div className="lbl" style={{ color: 'var(--warn)', marginBottom: 2 }}>Unverified address</div>
              <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)' }}>
                Derived from {contact.email_source || contact.email_pattern?.pattern || 'the company email pattern'}. Nobody has confirmed it.
              </div>
              <button
                className="btn"
                style={{ marginTop: 7 }}
                onClick={() => setForm((f) => ({ ...f, email_status: 'verified' }))}
              >
                Mark as confirmed
              </button>
            </div>
          ) : null}

          {form.email && form.email_status === 'verified' ? (
            <div className="card-sub" style={{ color: 'var(--pos)' }}>Address confirmed.</div>
          ) : null}

          {!isNew && contact.origin === 'research' ? (
            <div className="card-sub">
              Found by the research. Anything you add here is kept alongside it and never overwrites the record.
            </div>
          ) : null}
        </div>

        <div className="modal-foot">
          {removable ? (
            <button className="btn" style={{ color: 'var(--neg)', borderColor: 'var(--neg)' }} onClick={remove} disabled={busy}>
              Remove
            </button>
          ) : null}
          <span className="grow" />
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={busy || !(form.name || '').trim()}>
            {busy ? 'Saving…' : 'Save contact'}
          </button>
        </div>
      </div>
    </>
  )
}
